import type { ColourFamily, StageShade, TerminalKind } from '@crm/contracts';
import { Check, X } from 'lucide-react';
import { cn } from '../../lib/utils.ts';
import { SHADE_DOT } from './stage-colours.ts';

/**
 * How a pipeline stage looks anywhere in the app (RELAY-948): the chip is tinted with the
 * pipeline type's colour family and the dot carries the stage's shade within it, so a Deal
 * Status can never be mistaken for a Project Stage. Tailwind only ships classes it can see in
 * the source, hence the literal tables rather than built class names.
 */
const CHIP: Record<ColourFamily, string> = {
  violet:
    'bg-violet-50 text-violet-900 border-violet-200 dark:bg-violet-950/60 dark:text-violet-100 dark:border-violet-800',
  emerald:
    'bg-emerald-50 text-emerald-900 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-100 dark:border-emerald-800',
  sky: 'bg-sky-50 text-sky-900 border-sky-200 dark:bg-sky-950/60 dark:text-sky-100 dark:border-sky-800',
  rose: 'bg-rose-50 text-rose-900 border-rose-200 dark:bg-rose-950/60 dark:text-rose-100 dark:border-rose-800',
  amber:
    'bg-amber-50 text-amber-900 border-amber-200 dark:bg-amber-950/60 dark:text-amber-100 dark:border-amber-800',
};

export function StageChip({
  family,
  colour,
  name,
  terminalKind = null,
  className,
}: {
  family: ColourFamily;
  colour: StageShade;
  name: string;
  terminalKind?: TerminalKind | null;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex max-w-full min-w-0 items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap',
        CHIP[family],
        className,
      )}
      data-family={family}
      data-shade={colour}
      // In a narrow place (a compact pipeline column) the name is cut with an ellipsis; the
      // title keeps the whole of it a hover away.
      title={name}
    >
      <span className={cn('size-2 shrink-0 rounded-full', SHADE_DOT[family][colour])} />
      <span className="min-w-0 truncate">{name}</span>
      {terminalKind === 'won' && <Check aria-label="won" className="size-3 shrink-0" />}
      {terminalKind === 'lost' && <X aria-label="lost" className="size-3 shrink-0" />}
    </span>
  );
}
